<template>
  <div class="q-px-md q-py-sm">
    <FullCalendar ref="fullCalendar" :options="calendarOptions" />

    <!-- Day Events Dialog -->
    <q-dialog v-model="dialogDayEvents">
      <q-card style="width: 400px">
        <div class="row items-center q-py-sm">
          <div class="col text-center text-h6">{{ dayDate }}</div>
        </div>
        <q-separator />

        <div
          class="scroll q-pa-md"
          :style="`max-height: ${store.state.pageHeight - 200}px`"
        >
          <div v-for="(event, i) in dayEvents" :key="(event.date = i)">
            <div
              class="rounded-borders text-white q-px-xs q-mb-xs"
              :style="`background-color: ${event.backgroundColor}`"
            >
              {{ event.fullText }}
            </div>
          </div>
        </div>
        <q-separator />

        <div class="row justify-end q-pa-md">
          <q-btn label="Done" color="grey-2" text-color="black" v-close-popup />
        </div>
      </q-card>
    </q-dialog>

    <!-- Filter Dialog -->
    <q-dialog v-model="dialogFilter" persistent position="top">
      <q-card style="width: 300px">
        <div class="row items-center q-pa-md">
          <div class="col text-center text-h5">Filter</div>
          <div class="col-auto">
            <q-btn icon="close" color="primary" v-close-popup />
          </div>
        </div>
        <q-separator />

        <div class="q-pa-md">
          <div v-for="(option, key) in filterOptions" :key="key">
            <q-toggle
              v-model="option.display"
              :label="option.label"
              color="primary"
              @input="setEventFilter(key)"
            />
          </div>
        </div>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import store from '@/store';
import {
  decoder,
  numberWithCommas,
  formatDollars,
  isNumValid,
  cloneObj,
  getPercentage,
  getScrollbarWidth,
  smartCompare
} from '@/lib/helpers';
import { date } from 'quasar';
import AuthUtils from '@/lib/auth-utils';
import { upperCaseFirst } from '@/lib/helpers';

export default {
  name: 'CalendarPage',
  components: {
    FullCalendar
  },
  data() {
    return {
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        events: [],
        eventOrder: (a, b) => {
          return a.priority - b.priority;
        },
        height: store.state.pageHeight - 75,
        dayMaxEventRows: true,
        views: {
          dayGrid: {
            dayMaxEventRows: 3
          }
        },
        dateClick: (info) => {
          this.displayDate(info.dateStr);
        },
        datesSet: (info) => {
          this.setDayMaxEventRows(info);
        },
        eventClick: (info) => {
          const dateStr = date.formatDate(info.event.start, 'YYYY-MM-DD');
          this.displayDate(dateStr);
        },
        buttonText: {
          today: 'Today',
          month: 'Month',
          week: 'Week'
        },
        customButtons: {
          filterButton: {
            text: 'Filter',
            click: () => {
              this.dialogFilter = true;
            }
          },
          noteButton: {
            text: 'Add Note',
            click: () => {
              this.addNote();
            }
          }
        },
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'filterButton dayGridMonth,dayGridWeek'
          // right: 'filterButton noteButton dayGridMonth,dayGridWeek'
        }
      },
      dayDate: '',
      dayEvents: [],
      dialogDayEvents: false,
      dialogFilter: false,
      filterOptions: {
        allEvents: {
          display: true,
          label: 'All Events'
        },
        feed: {
          display: true,
          label: 'Feeding'
        },
        feedDelivery: {
          display: true,
          label: 'Feed Delivery'
        },
        stock: {
          display: true,
          label: 'Stock'
        },
        harvest: {
          display: true,
          label: 'Harvest'
        },
        loss: {
          display: true,
          label: 'Loss'
        },
        transfer: {
          display: true,
          label: 'Transfer'
        },
        treat: {
          display: true,
          label: 'Treat'
        },
        oxygen: {
          display: true,
          label: 'Oxygen'
        },
        flavor: {
          display: true,
          label: 'Flavor'
        }
      },
      store
    };
  },
  mounted() {
    this.setEvents();
    const pondGrainIds = [];
    for (const pond of store.state.farm.ponds) {
      pondGrainIds.push(pond.grain_id);
    }

    const queryObj = {
      storeInfo: {
        farmId: store.state.farm.id,
        action: 'getAllPondFeedings',
        queryProp: 'allFeedings'
      },
      pondGrainIds
    };

    store.dispatch('queryData', queryObj);
  },
  methods: {
    addNote() {
      console.log('%cadd note', 'color: orange');
    },
    displayDate(dateStr) {
      // account for timezone offset
      const [year, month, day] = dateStr.split('-').map(Number);

      this.dayDate = date.formatDate(
        new Date(year, month - 1, day),
        'MMM, D YYYY'
      );

      // for some reason filter needs to be done after clone
      const events = cloneObj(this.calendarOptions.events);

      this.dayEvents = events
        .filter((x) => x.date === dateStr)
        .sort((a, b) => {
          return a.priority - b.priority;
        });

      this.dialogDayEvents = true;
    },
    setDayMaxEventRows(info) {
      if (info.view.type === 'dayGridDay') {
        this.calendarOptions.views.dayGrid.dayMaxEventRows = this.$q.screen.xs
          ? 12
          : 20;
      } else if (info.view.type === 'dayGridWeek') {
        this.calendarOptions.views.dayGrid.dayMaxEventRows = this.$q.screen.xs
          ? 10
          : 16;
      } else {
        this.calendarOptions.views.dayGrid.dayMaxEventRows = this.$q.screen.xs
          ? 2
          : 3;
      }
    },
    setEvents() {
      this.calendarOptions.events = [];
      const feedingEvents = new Map();
      const noteEvents = new Map();

      for (const journal of store.state.dailyJournals) {
        if (journal.calendar_display) {
          const dateStr = date.formatDate(journal.date * 1000, 'YYYY-MM-DD');
          const subject =
            journal.subject.length > 0 ? journal.subject + ': ' : '';

          const keyword =
            journal.keyword.length > 0 ? journal.keyword + ' - ' : '';

          const journalEntry =
            journal.journal_entry.length > 100
              ? journal.journal_entry.substring(0, 100) + '...'
              : journal.journal_entry;

          const journalStr = subject + keyword + journalEntry;
          const journalStrFull = subject + keyword + journal.journal_entry;

          this.calendarOptions.events.push({
            title: decoder(journalStr),
            fullText: decoder(journalStrFull),
            priority: 1,
            date: dateStr,
            backgroundColor: '#3d9970',
            borderColor: '#3d9970'
          });
        }
      }

      for (const pond of store.state.farm.ponds) {
        for (const feeding of pond.feedings) {
          const dateStr = date.formatDate(feeding.date * 1000, 'YYYY-MM-DD');

          if (feedingEvents.has(dateStr)) {
            feedingEvents.set(
              dateStr,
              feedingEvents.get(dateStr) + feeding.pounds
            );
          } else {
            feedingEvents.set(dateStr, feeding.pounds);
          }
        }

        for (const historyObj of pond.growth.ManageHistory) {
          const dateStr = date.formatDate(historyObj.date * 1000, 'YYYY-MM-DD');

          if (this.filterOptions.stock.display) {
            if (historyObj.transfer_type === 'Stock') {
              const stockStr = `Stock: ${pond.name} - ${
                historyObj.transfer_name
              }
                ${numberWithCommas(historyObj.head)} head @ ${
                historyObj.size
              } lbs
                ${historyObj.memo}`;

              const decodedStr = decoder(stockStr);

              this.calendarOptions.events.push({
                title: decodedStr,
                fullText: decodedStr,
                priority: 2,
                date: dateStr,
                backgroundColor: '#1c3d5a',
                borderColor: '#1c3d5a'
              });
            }
          }

          if (this.filterOptions.harvest.display) {
            if (historyObj.transfer_type === 'Harvest') {
              const harvestStr = `Harvest: ${pond.name} - ${
                historyObj.transfer_name
              }
                ${numberWithCommas(historyObj.head)} head @ ${
                historyObj.size
              } lbs
                ${historyObj.memo}`;

              const decodedStr = decoder(harvestStr);

              this.calendarOptions.events.push({
                title: decodedStr,
                fullText: decodedStr,
                priority: 3,
                date: dateStr,
                backgroundColor: '#228b22',
                borderColor: '#228b22'
              });
            }
          }

          if (this.filterOptions.loss.display) {
            if (historyObj.transfer_type === 'Loss') {
              const mortalityStr = `Loss: ${pond.name} - ${numberWithCommas(
                historyObj.head
              )} head @ ${historyObj.size} lbs - ${historyObj.memo}`;

              const mortalityDecoded = decoder(mortalityStr);

              this.calendarOptions.events.push({
                title: mortalityDecoded,
                fullText: mortalityDecoded,
                priority: 4,
                date: dateStr,
                backgroundColor: '#a41e39',
                borderColor: '#a41e39'
              });
            }
          }

          if (this.filterOptions.transfer.display) {
            if (historyObj.transfer_type === 'Transfer In') {
              const transferStr = `Transfer: ${historyObj.transfer_name} to ${
                pond.name
              }
                ${numberWithCommas(historyObj.head)} head @ ${
                historyObj.size
              } lbs
                ${historyObj.memo}`;

              const decodedStr = decoder(transferStr);

              this.calendarOptions.events.push({
                title: decodedStr,
                fullText: decodedStr,
                priority: 5,
                date: dateStr,
                backgroundColor: '#8b008b',
                borderColor: '#8b008b'
              });
            }
          }
        }

        if (this.filterOptions.treat.display) {
          for (const treatment of pond.treatHistory) {
            const dateStr = date.formatDate(
              treatment.date * 1000,
              'YYYY-MM-DD'
            );
            const treatmentStr = `Treat: ${pond.name} - ${treatment.qty} ${treatment.size}
              ${treatment.name}`;

            const decodedStr = decoder(treatmentStr);

            this.calendarOptions.events.push({
              title: decodedStr,
              fullText: decodedStr,
              priority: 6,
              date: dateStr,
              backgroundColor: '#4b2e83',
              borderColor: '#4b2e83'
            });
          }
        }

        if (this.filterOptions.oxygen.display) {
          for (const oxygenRecord of pond.oxygenRecords) {
            const dateStr = date.formatDate(
              oxygenRecord.date * 1000,
              'YYYY-MM-DD'
            );
            const noteStr =
              oxygenRecord.memo.length > 0 ? `Notes: ${oxygenRecord.memo}` : '';

            const oxygenStr = `Oxygen: ${pond.name}
              Handheld: ${oxygenRecord.handheld_reading}
              Monitor: ${oxygenRecord.monitor_reading}
              ${noteStr}`;

            const decodedStr = decoder(oxygenStr);

            this.calendarOptions.events.push({
              title: decodedStr,
              fullText: decodedStr,
              priority: 7,
              date: dateStr,
              backgroundColor: '#b8860b',
              borderColor: '#b8860b'
            });
          }
        }

        if (this.filterOptions.flavor.display) {
          for (const flavor of pond.flavorHistory) {
            const dateStr = date.formatDate(flavor.date * 1000, 'YYYY-MM-DD');
            const noteStr =
              flavor.memo.length > 0 ? `Notes: ${flavor.memo}` : '';

            const flavorStr = `Flavor: ${pond.name} - ${flavor.flavor_name}
              Severity: ${flavor.severity} - ${flavor.processor}
              ${noteStr}`;

            const decodedStr = decoder(flavorStr);

            this.calendarOptions.events.push({
              title: decodedStr,
              fullText: decodedStr,
              priority: 8,
              date: dateStr,
              backgroundColor: '#b34700',
              borderColor: '#b34700'
            });
          }
        }
      }

      if (this.filterOptions.feedDelivery.display) {
        for (const bin of store.state.farm.bins) {
          for (const delivery of bin.deliveries) {
            // Mill-only, receiver-side: dedupes bin-to-bin transfers that appear on both bins.
            if (delivery.to_account !== bin.grain_id || !delivery.mill_name) {
              continue;
            }

            const dateStr = date.formatDate(delivery.date * 1000, 'YYYY-MM-DD');
            const tons = numberWithCommas(
              Math.round((delivery.pounds / 2000) * 100) / 100
            );
            const feedType = delivery.feed_type_name
              ? ` ${delivery.feed_type_name}`
              : '';
            const priceLine =
              delivery.price_per_pound > 0
                ? `\n${formatDollars(
                    delivery.pounds * delivery.price_per_pound
                  )} @ ${formatDollars(delivery.price_per_pound * 2000)}/ton`
                : '';
            const memoLine = delivery.memo ? `\n${delivery.memo}` : '';

            const deliveryStr = `Delivery: ${bin.name} - ${delivery.mill_name}
              ${tons} tons${feedType}${priceLine}${memoLine}`;

            const decodedStr = decoder(deliveryStr);

            this.calendarOptions.events.push({
              title: decodedStr,
              fullText: decodedStr,
              priority: 9,
              date: dateStr,
              backgroundColor: '#5a4a1f',
              borderColor: '#5a4a1f'
            });
          }
        }
      }

      // Add in all the rest of the feedings
      // This will go back 10 years
      // We do want to overwrite the feedings that are
      // already in the calendar here
      const allFeedingEvents = new Map();
      for (const feeding of store.state.dataQueries.allFeedings) {
        const dateStr = date.formatDate(feeding.date * 1000, 'YYYY-MM-DD');

        if (allFeedingEvents.has(dateStr)) {
          allFeedingEvents.set(
            dateStr,
            allFeedingEvents.get(dateStr) + feeding.pounds
          );
        } else {
          allFeedingEvents.set(dateStr, feeding.pounds);
        }
      }

      for (const [key, val] of Object.entries(allFeedingEvents)) {
        feedingEvents.set(key, val);
      }

      if (this.filterOptions.feed.display) {
        for (const [dateStr, pounds] of feedingEvents) {
          this.calendarOptions.events.push({
            title: `Feed: ${numberWithCommas(pounds)} lbs`,
            fullText: `Feed: ${numberWithCommas(pounds)} lbs`,
            priority: 10,
            date: dateStr,
            backgroundColor: '#8b6508',
            borderColor: '#8b6508'
          });
        }
      }
    },
    setEventFilter(key) {
      if (key === 'allEvents') {
        for (const option of Object.values(this.filterOptions)) {
          option.display = this.filterOptions.allEvents.display;
        }
      } else {
        if (this.filterOptions[key].display === false) {
          this.filterOptions.allEvents.display = false;
        }
      }

      this.setEvents();
    }
  },
  watch: {
    '$store.state.farm.ponds': {
      handler() {
        this.setEvents();
      },
      deep: true
    },
    '$store.state.farm.bins': {
      handler() {
        this.setEvents();
      },
      deep: true
    },
    '$store.state.dataQueries.allFeedings': {
      handler() {
        this.setEvents();
      },
      deep: true
    }
  }
};
</script>

<style>
.fc-button.fc-button-primary {
  color: #fff;
  background: #245ad3;
  border-color: #517ee1;
}

.fc-button.fc-button-primary:hover {
  color: #fff;
  background: #517ee1;
  border-color: #517ee1;
}

.fc-button.fc-button-primary:focus {
  box-shadow: none !important;
}

.fc-today-button.fc-button.fc-button-primary {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-today-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-prev-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-filterButton-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-noteButton-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-next-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #245ad3;
}

.fc-dayGridMonth-button.fc-button.fc-button-primary.fc-button-active {
  color: #fff;
  background: #245ad3;
  border-color: #517ee1;
}

.fc-dayGridWeek-button.fc-button.fc-button-primary.fc-button-active {
  color: #fff;
  background: #245ad3;
  border-color: #517ee1;
}

.fc-dayGridMonth-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #517ee1;
}

.fc-dayGridWeek-button.fc-button.fc-button-primary:active {
  color: #fff;
  background: #245ad3;
  border-color: #517ee1;
}

@media (max-width: 700px) {
  .fc .fc-toolbar.fc-header-toolbar {
    display: block;
    text-align: center;
  }

  .fc-header-toolbar .fc-toolbar-chunk {
    display: block;
  }
}

.fc-event {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4 !important;
  white-space: pre-line !important;
}

.fc-sticky {
  padding: 0 0 0 3px !important;
}
</style>
